/* eslint-disable react-native/no-inline-styles */
/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { StyleSheet, View, ScrollView, TouchableOpacity } from 'react-native';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';

import * as Layout from '@components/screen/Layout';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';

import { SociollaForm, SociollaSelectBox } from '@components/sociolla/form';
import BackButton from '@components/navbar/BackButton';

import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import { getBrandDetail, updateBrand } from '@api/ms-catalog';
import useApi from '@/hooks/useApi';
import {
  hasNoAccess,
  getConstant,
  changeTimeZone,
  isVn,
} from '@global/helpers';
import { showPopup } from '@/store/modal';

const validationSchema = Yup.object().shape({
  applicable_for: Yup.mixed().required('Brand is required'),
  brand_type: Yup.string().required('Brand type is required'),
  brand_status: Yup.string().required('Brand status is required'),
});

const BRAND_TYPE_OPTIONS = [
  { label: 'Internal', value: 'internal' },
  { label: 'External', value: 'external' },
  { label: 'Owned', value: 'owned' },
];

const BRAND_STATUS_OPTIONS = [
  { label: 'New', value: 'new' },
  { label: 'Existing', value: 'existing' },
];

const RadioRow = ({
  label,
  name,
  options,
  error,
  touched,
  value,
  onChange,
  onValueChange,
}) => {
  const handlePress = (val) => {
    onChange(name, val);
    if (onValueChange) {
      onValueChange();
    }
  };

  return (
    <View style={{ marginBottom: 5 }}>
      <SociollaText style={styles.radioLabel}>{label}</SociollaText>

      <View style={styles.radioRow}>
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <TouchableOpacity
              key={option.value}
              style={[styles.radioOption, !isWeb && styles.radioOptionMobile]}
              onPress={() => handlePress(option.value)}
              activeOpacity={0.8}>
              <View
                style={[
                  styles.radioCircle,
                  isSelected
                    ? styles.radioCircleSelected
                    : styles.radioCircleUnselected,
                ]}
              />

              <SociollaText style={styles.radioText}>
                {option.label}
              </SociollaText>
            </TouchableOpacity>
          );
        })}
      </View>

      {error && touched && (
        <SociollaText style={styles.errorMessage}>{error}</SociollaText>
      )}
    </View>
  );
};

RadioRow.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  error: PropTypes.string,
  touched: PropTypes.any,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  onValueChange: PropTypes.func,
};

const BrandFormFields = ({ isEdit, isView, onRadioStateChange }) => {
  const { values, errors, touched, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (onRadioStateChange) {
      onRadioStateChange(values.brand_type, values.brand_status);
    }
  }, [values.brand_type, values.brand_status, onRadioStateChange]);

  return (
    <>
      <Layout.FormGroup>
        <SociollaSelectBox
          name="applicable_for"
          label="Brand"
          trackBy="value"
          labelKey="label"
          options={getConstant('brand')}
          disabled={isEdit || isView}
          editable={!isEdit && !isView}
          isRequired
        />
      </Layout.FormGroup>

      <Layout.FormGroup>
        <RadioRow
          label="Brand Type*"
          name="brand_type"
          options={BRAND_TYPE_OPTIONS}
          value={values.brand_type}
          error={errors.brand_type}
          touched={touched.brand_type}
          onChange={setFieldValue}
        />
      </Layout.FormGroup>

      <Layout.FormGroup>
        <RadioRow
          label="Brand Status*"
          name="brand_status"
          options={BRAND_STATUS_OPTIONS}
          value={values.brand_status}
          error={errors.brand_status}
          touched={touched.brand_status}
          onChange={setFieldValue}
        />
      </Layout.FormGroup>
    </>
  );
};

BrandFormFields.propTypes = {
  isEdit: PropTypes.bool,
  isView: PropTypes.bool,
  onRadioStateChange: PropTypes.func,
};

const BrandDetailScreen = (props) => {
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const [radioState, setRadioState] = useState({
    brand_type: '',
    brand_status: '',
  });

  const brand = useApi(getBrandDetail);
  const formikRef = useRef(null);

  const dispatch = useDispatch();

  const hasAccess = !hasNoAccess([
    'superadmin',
    'sales_admin',
    'marketing_admin',
    'general',
  ]);

  const disabledButton = useMemo(() => {
    return !(radioState.brand_type && radioState.brand_status);
  }, [radioState.brand_type, radioState.brand_status]);

  const button = useMemo(() => {
    if (!isView && hasAccess) {
      return [
        {
          title: 'Save',
          disabled: isEdit ? false : disabledButton,
          onPress: () => formikRef.current?.handleSubmit(),
        },
      ];
    }
    return [];
  }, [isEdit, disabledButton, hasAccess, isView]);

  const initialValues = useMemo(() => {
    const isVietnam = isVn();
    if (isVietnam) {
      const type =
        brand.data?.collaboration_type_vn ??
        brand.data?.collaboration_type_vn ??
        '';

      const status =
        brand.data?.collaboration_status_vn ??
        brand.data?.collaboration_status_vn ??
        '';

      return {
        applicable_for: isEdit ? props.route.params?.name || '' : '',
        brand_type: isEdit ? type : '',
        brand_status: isEdit ? status : '',
      };
    } else {
      const type =
        brand.data?.collaboration_type ?? brand.data?.collaboration_type ?? '';

      const status =
        brand.data?.collaboration_status ??
        brand.data?.collaboration_status ??
        '';

      return {
        applicable_for: isEdit ? props.route.params?.name || '' : '',
        brand_type: isEdit ? type : '',
        brand_status: isEdit ? status : '',
      };
    }
  }, [isEdit, brand.data, props.route.params?.name]);

  const handleRadioStateChange = useCallback((brandType, brandStatus) => {
    setRadioState((prev) => {
      if (prev.brand_type === brandType && prev.brand_status === brandStatus) {
        return prev;
      }
      return { brand_type: brandType, brand_status: brandStatus };
    });
  }, []);

  const handleSubmit = async (data) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const brand_id = brand.data?._id;
      const isVietnam = isVn();
      if (isVietnam) {
        const payload = {
          collaboration_type_vn: data.brand_type,
          collaboration_status_vn: data.brand_status,
        };

        if (isEdit) {
          const prevType = brand.data?.collaboration_type_vn || '';
          const prevStatus = brand.data?.collaboration_status_vn || '';

          if (
            payload.collaboration_type_vn !== prevType ||
            payload.collaboration_status_vn !== prevStatus
          ) {
            const res = await updateBrand(brand_id, payload);
            if (res.status === 200) {
              Toast.show({ text1: 'Successfully saved', type: 'success' });
            }
            brand.updateData(res.data.data);
          }
        }
      } else {
        const payload = {
          collaboration_type: data.brand_type,
          collaboration_status: data.brand_status,
        };

        if (isEdit) {
          const prevType = brand.data?.collaboration_type || '';
          const prevStatus = brand.data?.collaboration_status || '';

          if (
            payload.collaboration_type !== prevType ||
            payload.collaboration_status !== prevStatus
          ) {
            const res = await updateBrand(brand_id, payload);
            if (res.status === 200) {
              Toast.show({ text1: 'Successfully saved', type: 'success' });
            }
            brand.updateData(res.data.data);
          }
        }
      }

      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (e) {
      let message = 'A system error occurred';
      if (e?.response?.data?.message) {
        message = e.response.data.message;
      }
      if (message.includes('duplicate')) {
        message = 'Duplicate types of brand';
      }

      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({ text1: message, type: 'error' });
    }
  };

  useEffect(() => {
    const brandId = props.route.params?.id || '';

    async function onLoad() {
      if (brandId && (brandId === 'view' || brandId === 'add')) {
        brand.updateData({});
        setIsView(Boolean(brandId === 'view'));
        setIsReady(true);
      } else if (brandId) {
        const res = await brand.request(brandId);
        if (res.status === 200) {
          setIsEdit(true);
          setTimeout(() => setIsReady(true), 500);
        }
      }
    }

    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  useEffect(() => {
    if (props.navigation) {
      props.navigation.setOptions({
        headerLeft: () => (
          <BackButton
            {...props}
            onPress={() => {
              props.navigation.navigate('BrandScreen');
            }}
          />
        ),
      });
    }
  }, [props]);

  if (!isReady) {
    return (
      <Layout.Container>
        <Layout.Wrapper rounded>
          <Layout.Loader />
        </Layout.Wrapper>
      </Layout.Container>
    );
  }

  return (
    <Layout.Container>
      {isWeb ? (
        <Layout.Wrapper fullWidth white={false} hasProgressBar={true}>
          <Layout.Back
            title="Brand"
            onCallback={() => props.navigation.navigate('BrandScreen')}
          />
          <Layout.Title title={isEdit ? 'Update Brand' : 'Create Brand'}>
            {!isView && hasAccess && (
              <Button
                rounded={true}
                buttonStyle={{ width: 120 }}
                disabledStyle={{ width: 120 }}
                disabled={disabledButton}
                title={
                  <SociollaText
                    type="bold"
                    style={{
                      fontSize: 14,
                      letterSpacing: 0.5,
                      color: Colors.white,
                    }}>
                    {isEdit ? 'Save' : 'Add'}
                  </SociollaText>
                }
                onPress={() => formikRef.current?.handleSubmit()}
              />
            )}
          </Layout.Title>
        </Layout.Wrapper>
      ) : null}

      <Layout.Wrapper
        rounded
        hasProgressBar
        bottomSpace={!isWeb ? 70 : 0}
        fullWidth
        isHeight>
        <ScrollView>
          <Layout.FormWrapper
            style={{
              flexDirection: isWeb ? 'row' : 'column',
              paddingHorizontal: 15,
              ...(isWeb
                ? { paddingTop: 20, paddingVertical: 20 }
                : { paddingTop: 10 }),
            }}>
            <View style={{ width: isWeb ? '50%' : '100%' }}>
              <SociollaForm
                initialValues={initialValues}
                enableReinitialize={isEdit && !!brand.data?._id}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                innerRef={(formik) => {
                  formikRef.current = formik;
                }}>
                <BrandFormFields
                  isEdit={isEdit}
                  isView={isView}
                  onRadioStateChange={handleRadioStateChange}
                />
              </SociollaForm>
            </View>

            <View style={{ width: isWeb ? '50%' : '100%' }} />
          </Layout.FormWrapper>
        </ScrollView>
      </Layout.Wrapper>

      {!isWeb && (
        <Layout.ButtonSticky show route={props?.route} buttons={button} />
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  radioLabel: {
    fontSize: 12,
    color: Colors.black3,
  },

  radioRow: {
    flexDirection: 'row',
    marginTop: 10,
  },

  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    width: 160,
    marginRight: 24,
  },

  // Equal thirds so Brand Type and Brand Status options line up in columns
  radioOptionMobile: {
    width: '33.33%',
    marginRight: 0,
  },

  radioCircle: {
    height: 20,
    width: 20,
    borderRadius: 20,
    backgroundColor: Colors.white,
    marginRight: 12,
  },

  radioCircleUnselected: {
    borderColor: Colors.grey10,
    borderWidth: 2,
  },

  radioCircleSelected: {
    borderColor: Colors.blue10,
    borderWidth: 4,
  },

  radioText: {
    fontSize: 12,
  },

  errorMessage: {
    fontSize: 12,
    color: Colors.red1,
    marginTop: 5,
  },
});

export default BrandDetailScreen;
